import { useState, useRef, useEffect, useMemo } from 'react';
import './styles.scss';
import cx from 'classnames';
import { isMobile } from 'react-device-detect';
import { usePermission } from '../../../../../container/permissionContext';
import { Tooltip } from 'react-tooltip';
import { useDrag, useDrop } from 'react-dnd';
import BothHalvesCell from './components/BothHalvesCell';
import FirstHalfCell from './components/FirstHalfCell';
import SecondHalfCell from './components/SecondHalfCell';

const SchedulingCell = ({
  cell,
  getPeriodData,
  periodData,
  editSidebarAction,
  editSidebarFormData,
  editSidebarSelectedUser,
  editSidebarSelectedItems,
  setEditSidebarSelectedItems,
  isPrint,
  isPastPeriod,
  role,
  unit,
  shiftsData,
  setCellAssignmentData,
  name,
  showRecurringShifts,
  cellAssignmentData,
  isSelectingPostShiftsActive,
  setSelectedShiftsToPost,
  selectedShiftsToPost,
}) => {
  const { hasPermissionByCodeName } = usePermission();
  const hasWritePermission = hasPermissionByCodeName('scheduling_management');

  const [, drop] = useDrop(
    () => ({
      accept: ['scheduling_full_halves_assignment'],
      canDrop: () => hasWritePermission,
      drop: () => ({ cell }),
    }),
    [hasWritePermission],
  );

  const isEmptyCell = useMemo(() => {
    return (
      !cell.both_halves_user &&
      !cell.first_half_user &&
      !cell.second_half_user &&
      !cell.is_both_halves_posted &&
      !cell.is_first_half_posted &&
      !cell.is_second_half_posted &&
      !editSidebarSelectedItems?.find((item) => item.cell.id === cell.id)
    );
  }, [cell, editSidebarSelectedItems]);

  return (
    <>
      {isEmptyCell && <div ref={drop} className="empty-cell" style={{ width: '100%', height: '100%' }}></div>}
      {cell.both_halves_user && !showRecurringShifts && (
        <BothHalvesCell
          periodData={periodData}
          editSidebarSelectedItems={editSidebarSelectedItems}
          editSidebarAction={editSidebarAction}
          editSidebarFormData={editSidebarFormData}
          setEditSidebarSelectedItems={setEditSidebarSelectedItems}
          editSidebarSelectedUser={editSidebarSelectedUser}
          getPeriodData={getPeriodData}
          isPrint={isPrint}
          cell={cell}
          cellAssignmentData={cellAssignmentData}
          setCellAssignmentData={setCellAssignmentData}
          hasWritePermission={hasWritePermission}
          showRecurringShifts={showRecurringShifts}
          unit={unit}
          role={role}
          shiftsData={shiftsData}
          isPastPeriod={isPastPeriod}
          isSelectingPostShiftsActive={isSelectingPostShiftsActive}
          setSelectedShiftsToPost={setSelectedShiftsToPost}
          name={name}
        />
      )}
      {(cell.first_half_user ||
        cell.second_half_user ||
        cell.is_first_half_posted ||
        cell.is_second_half_posted ||
        editSidebarSelectedItems?.find(
          (item) => item.cell.id === cell.id && (item.halfPart === 'first_half' || item.halfPart === 'second_half'),
        )) && (
        <div className="cell-assignment-wrapper">
          <FirstHalfCell
            periodData={periodData}
            editSidebarSelectedItems={editSidebarSelectedItems}
            editSidebarAction={editSidebarAction}
            editSidebarFormData={editSidebarFormData}
            setEditSidebarSelectedItems={setEditSidebarSelectedItems}
            editSidebarSelectedUser={editSidebarSelectedUser}
            getPeriodData={getPeriodData}
            cell={cell}
            isPrint={isPrint}
            showRecurringShifts={showRecurringShifts}
            cellAssignmentData={cellAssignmentData}
            setCellAssignmentData={setCellAssignmentData}
            unit={unit}
            role={role}
            shiftsData={shiftsData}
            isPastPeriod={isPastPeriod}
            hasWritePermission={hasWritePermission}
            isSelectingPostShiftsActive={isSelectingPostShiftsActive}
            setSelectedShiftsToPost={setSelectedShiftsToPost}
            name={name}
            selectedShiftsToPost={selectedShiftsToPost}
          />

          <div style={{ height: '100%', width: '1px', backgroundColor: '#cbd2e0', margin: '0 2px' }}></div>
          {!!cell.second_half_user && !isPrint && (
            <Tooltip
              opacity={1}
              id={`user-tooltip-${cell.second_half_user.id}`}
              place="top"
              content={
                <>
                  <span className="cell-assignment-user-name no-hide">{cell.second_half_user.user_name}</span>
                  <span className="cell-assignment-user-note no-hide">{cell.second_half_user?.notes || ''}</span>
                </>
              }
            />
          )}

          <SecondHalfCell
            periodData={periodData}
            editSidebarSelectedItems={editSidebarSelectedItems}
            setEditSidebarSelectedItems={setEditSidebarSelectedItems}
            editSidebarAction={editSidebarAction}
            editSidebarFormData={editSidebarFormData}
            editSidebarSelectedUser={editSidebarSelectedUser}
            cell={cell}
            getPeriodData={getPeriodData}
            cellAssignmentData={cellAssignmentData}
            showRecurringShifts={showRecurringShifts}
            isPrint={isPrint}
            hasWritePermission={hasWritePermission}
            setCellAssignmentData={setCellAssignmentData}
            unit={unit}
            role={role}
            shiftsData={shiftsData}
            isPastPeriod={isPastPeriod}
            isSelectingPostShiftsActive={isSelectingPostShiftsActive}
            setSelectedShiftsToPost={setSelectedShiftsToPost}
            name={name}
            selectedShiftsToPost={selectedShiftsToPost}
          />
        </div>
      )}
      {cell.is_both_halves_posted && !showRecurringShifts && (
        <div
          className={cx('posted-cell', {})}
          // Pickup requests are handled from the shift's column header (AssignmentRequestsModal).
          onClick={
            isPrint || !hasWritePermission || isSelectingPostShiftsActive || !!editSidebarAction || isMobile
              ? undefined
              : () =>
                  setCellAssignmentData({
                    unit,
                    cell,
                    roleName: name,
                    roleId: role.id,
                    isPermanentRole: !!role.permanent_role,
                    shiftLabel: shiftsData[cell.shift_order],
                    available: 'both_halves',
                    to_post: 'both_halves',
                    isPosted: true,
                    halfPart: 'both_halves',
                    isPastPeriod,
                    roleType: role.role,
                    isPrint: isPrint,
                  })
          }
        >
          Posted
        </div>
      )}
    </>
  );
};

export default SchedulingCell;
